/**
 * pointerGestures.ts - Mouse-driven selection, row and column reordering and fill-down drags. Private to
 * `createTableInteractions`.
 */

import $ from 'jquery';
import isEqual from 'react-fast-compare';

import {assert} from 'Asserts';
import checkSoftInvariant from 'lib/checkSoftInvariant';
import * as MultiSelectListUtil from 'notebook/mediocre/selection/block/MultiSelectListUtil';
import {
  MultiSelectionBox,
  TABLE_SELECTION_ROW,
  TableDimension,
  TableIndex,
} from 'notebook/tables/constants/TableSelectionConstants';
import type {TableEditableState} from 'notebook/tables/core-ui/TableEditableState';
import type {TableDom} from 'notebook/tables/store/logic/interactions/tableDom';
import type {
  CellDragStartArgs,
  TableSelection,
} from 'notebook/tables/store/logic/interactions/tableSelection';
import {computeFillDownDragBox} from 'notebook/tables/store/logic/TableFillDownLogic';
import {computeCellIndexFromCid} from 'notebook/tables/store/logic/TableIdentityLogic';
import {computeContainsMergedRegion} from 'notebook/tables/store/logic/TableMergedCellLogic';
import {computeExpandedSelectionIndex} from 'notebook/tables/store/logic/TableMultiSelectionLogic';
import {computeExpandedSelectionBox} from 'notebook/tables/store/logic/TableSelectionLogic';
import type {TableModel} from 'notebook/tables/store/models/TableModel';
import type {TableProps} from 'notebook/tables/store/TableStore';
import TableUtil from 'notebook/tables/TableUtil';
import {ElementMouseEvent} from 'util/Types';

export interface AxisCellDragStartArgs {
  start: TableIndex;
  active: string;
}

export interface PointerGestures {
  pointerDownOnCell: (args: CellDragStartArgs, e: ElementMouseEvent) => void;
  pointerOverCell: (index: TableIndex) => void;
  pointerMoveOverCell: (index: TableIndex) => void;
  pointerDownOnAxis: (options: AxisCellDragStartArgs, e: ElementMouseEvent) => void;
  pointerOverAxis: (index: TableIndex, e: ElementMouseEvent) => void;
  startReorder: (draggingDimension: TableDimension, index: number) => boolean;
  startFillDown: (e: ElementMouseEvent) => void;
  fillDownToBlankRows: (e: ElementMouseEvent) => void;
  trackPointer: (clientX: number, clientY: number) => void;
  /** Extends the in-progress drag to the cell left under the pointer by a scroll. */
  scrollSettled: () => void;
  /** Drops the window listeners. Called when the table unmounts. */
  stop: () => void;
}

export function createPointerGestures({
  dom,
  selection: tableSelection,
  getTableModel,
  getTableProps,
  setState,
  isMounted,
  focusBearer,
}: {
  dom: TableDom;
  selection: TableSelection;
  getTableModel: () => TableModel;
  getTableProps: () => TableProps;
  setState: (state: Partial<TableEditableState>) => void;
  isMounted: () => boolean;
  focusBearer: () => HTMLDivElement;
}): PointerGestures {
  const pointerPosition: {clientX?: number; clientY?: number} = {};

  const setMultiExtra = (
    multiExtraUpdate: {
      fillDownActive?: boolean;
      fillDownInvalid?: boolean;
      fillDownBox?: MultiSelectionBox | null;
    },
    multiExtra: TableEditableState['multiExtra']
  ): void => {
    MultiSelectListUtil.setMultiExtra(multiExtraUpdate, setState, multiExtra);
  };

  const selectionBox = (): MultiSelectionBox => {
    const {expandedBox} = getTableModel().selection;
    assert(expandedBox != null, 'Expected to have a selection');
    return expandedBox;
  };

  /** The row range spanning the table's effective column slice, from row `i1` to `i2`. */
  const rowRange = (i1: number, i2: number): {start: TableIndex; end: TableIndex} => {
    const {start: sliceStart, end: sliceEnd} = getTableModel().singleInputs.effectiveSlices;
    return {start: [Math.min(i1, i2), sliceStart], end: [Math.max(i1, i2), sliceEnd]};
  };

  /** The column range spanning every row, from column `j1` to `j2`. */
  const colRange = (j1: number, j2: number): {start: TableIndex; end: TableIndex} => {
    const lastRowIndex = getTableModel().dimensions.rowCount - 1;
    return {start: [0, Math.min(j1, j2)], end: [lastRowIndex, Math.max(j1, j2)]};
  };

  const fillDownMouseUpHandler = (): void => {
    const {fillDown, state, mutations} = getTableModel();
    const reset = {fillDownActive: false, fillDownBox: null, fillDownInvalid: false};
    if (!fillDown.isActive || fillDown.isInvalid) {
      setMultiExtra(reset, state.multiExtra);
      return;
    }
    try {
      // If this for any reason errors, unset fillDownActive to allow normal selections.
      mutations.performFillDown();
      MultiSelectListUtil._focus(focusBearer());
    } finally {
      setMultiExtra(reset, state.multiExtra);
    }
  };

  const pointerOverCell = (index: TableIndex): void => {
    if (!isMounted()) {
      return;
    }
    const {fillDown, state, dimensions} = getTableModel();
    if (!fillDown.isActive && !state.draggingDimension) {
      tableSelection.extendSelection(
        computeExpandedSelectionIndex(index, state.multiActive, dimensions.rowCount, dimensions.columnCount)
      );
    }
  };

  const pointerMoveOverCell = (index: TableIndex): void => {
    if (!isMounted()) {
      return;
    }
    const {fillDown, state} = getTableModel();
    if (!fillDown.isActive) {
      return;
    }
    const {clientX, clientY} = pointerPosition;
    const fillDownBox = computeFillDownDragBox(index, selectionBox(), dom.getScrollAreaCellRect(index)!, {
      clientX: clientX ?? NaN,
      clientY: clientY ?? NaN,
    });

    // Each write below is based on the multiExtra at the start of this move, so a later write
    // replaces any earlier one rather than building on it.
    if (computeContainsMergedRegion(getTableProps().computedTable, fillDownBox)) {
      setMultiExtra({fillDownInvalid: true}, state.multiExtra);
      return;
    }
    setMultiExtra({fillDownInvalid: false}, state.multiExtra);

    if (!isEqual(fillDown.box, fillDownBox)) {
      setMultiExtra({fillDownBox}, state.multiExtra);
    }
  };

  const scrollSettled = (): void => {
    if (!isMounted()) {
      return;
    }
    const {clientX, clientY} = pointerPosition;
    const {state, dimensions} = getTableModel();
    if (!(state.multiActive && clientX != null && clientY != null)) {
      return;
    }
    const el = document.elementFromPoint(clientX, clientY) as Element;
    const $cell = $(el).closest('.cell');
    if (!$cell.length) {
      return;
    }
    const index = computeCellIndexFromCid(getTableProps().cid, $cell.attr('data-cid'));
    if (index == null || !TableUtil.isIndexBetween(dimensions.firstIndex, dimensions.lastIndex, index)) {
      // Skip ghost cells
      return;
    }
    pointerOverCell(index);
    pointerMoveOverCell(index);
  };

  const trackPointer = (clientX: number, clientY: number): void => {
    pointerPosition.clientX = clientX;
    pointerPosition.clientY = clientY;
  };

  return {
    pointerDownOnCell: tableSelection.beginSelection,
    pointerOverCell,
    pointerMoveOverCell,

    pointerDownOnAxis: (options, e) => {
      if (!isMounted()) {
        return;
      }
      const {singleInputs, state, dimensions} = getTableModel();
      let {start} = options;
      const {active} = options;
      const {start: sliceStart, end: sliceEnd} = singleInputs.slices;
      const isRowSelection = active === TABLE_SELECTION_ROW;
      // for row selection, if the table is sliced then start the row selection at the sliceStart
      if (isRowSelection && sliceStart) {
        start = [start[0], sliceStart ?? 0];
      }
      let end = computeExpandedSelectionIndex(start, active, dimensions.rowCount, dimensions.columnCount);
      // for row selection, if the table is sliced then end the row selection at the sliceEnd
      if (isRowSelection && sliceEnd) {
        end = [end[0], sliceEnd ?? end[1]];
      }

      // If we are extending the selection, take the existing selected index,
      // and project it to the start of the desired selection dimension.
      if (e.shiftKey && state.multiSelIndex != null) {
        start = isRowSelection ? [state.multiSelIndex[0], sliceStart ?? 0] : [0, state.multiSelIndex[1]];
      }

      tableSelection.beginSelection({start, end, active}, e);
    },

    pointerOverAxis: (index) => {
      if (!isMounted()) {
        return;
      }
      const {state, dimensions} = getTableModel();
      if (!state.draggingDimension) {
        tableSelection.extendSelection(
          computeExpandedSelectionIndex(index, state.multiActive, dimensions.rowCount, dimensions.columnCount)
        );
      }
    },

    startReorder: (draggingDimension, index) => {
      if (!isMounted()) {
        return false;
      }
      const {state, selection, singleInputs, dimensions} = getTableModel();
      if (state.draggingDimension || getTableProps().locked) {
        checkSoftInvariant(!state.draggingDimension, {
          message: 'Expected not to be dragging already',
          expirationDate: '2026-02-01',
          errorInDevAndTest: true,
        });
        return false;
      }
      MultiSelectListUtil.cancelComposition(focusBearer());

      let selectionStart = state.multiStartIndex;
      let selectionEnd = state.multiEndIndex;

      let isIndexSelected = false; // Whether any cells in the dragged row/column are selected
      if (selection.hasRealSelection) {
        const {minI, maxI, minJ, maxJ} = selectionBox();
        // Extend selection to include whole row/column
        if (draggingDimension === TableDimension.ROW) {
          ({start: selectionStart, end: selectionEnd} = rowRange(minI, maxI));
          // Whether the row is in the selection AND the selection isn't every row
          isIndexSelected =
            index >= minI && index <= maxI && !(minI === 0 && maxI === dimensions.rowCount - 1);
        } else {
          // The minJ for column reordering should begin only with columns that are not Single Input.
          const firstNonSingleInputIndex = singleInputs.boundaryIndex;
          const newMinJ = Math.max(minJ, firstNonSingleInputIndex ?? minJ);
          ({start: selectionStart, end: selectionEnd} = colRange(newMinJ, maxJ));
          const {start: sliceStart, end: sliceEnd} = singleInputs.effectiveSlices;
          // Whether the column is in the selection AND the selection isn't every column
          isIndexSelected = index >= minJ && index <= maxJ && !(minJ <= sliceStart && maxJ >= sliceEnd);
        }
      }

      if (!isIndexSelected) {
        // Reset the selection to only the row/column that is being dragged
        ({start: selectionStart, end: selectionEnd} =
          draggingDimension === TableDimension.ROW ? rowRange(index, index) : colRange(index, index));
      }

      // Ensure that the selection includes any merged cells
      const {table, computedTable} = getTableProps();
      const expandedBox = computeExpandedSelectionBox(selectionStart, selectionEnd, table, computedTable);
      assert(expandedBox != null, 'Expected to have a selection');
      const {minI, maxI, minJ, maxJ} = expandedBox;
      tableSelection.selectRange([minI, minJ], [maxI, maxJ]);

      setState({draggingDimension});
      return true;
    },

    startFillDown: (e) => {
      if (!isMounted()) {
        return;
      }
      e.preventDefault(); // prevent browser drag behavior from changing selection
      MultiSelectListUtil.cancelComposition(focusBearer());

      const {selection, state} = getTableModel();
      assert(selection.hasMultiSelection, 'Expected to have a selection');
      // Because the fill down is dependent on the multiSelectionBox, we store fillDown state alongside
      // multiSelectionBox state, so that if it is cleared, it also clears the fillDown state.
      setMultiExtra({fillDownActive: true, fillDownBox: selectionBox()}, state.multiExtra);
      $(window).one('mouseup', fillDownMouseUpHandler);
    },

    fillDownToBlankRows: (e) => {
      if (!isMounted()) {
        return;
      }
      e.preventDefault();
      getTableModel().mutations.performDoubleClickFillDown();
      MultiSelectListUtil._focus(focusBearer());
    },

    trackPointer,

    scrollSettled,

    stop: () => {
      $(window).off('mouseup', fillDownMouseUpHandler);
    },
  };
}
